import { useState } from 'react'

import { SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { DateValueI } from '@/components/ui'
import { Role } from '@/features/managed-accounts'
import { useFacilityNamesQuery, useUsersQuery } from '@/graphql/generated/hooks'
import { FacilityType, GuardTourStatus } from '@/graphql/generated/schemas'
import { MultiFilter } from '@/redux/types/filters'
import {
  selectGuardToursFacilityIdsFilter,
  selectGuardToursGuardIdsFilter,
  selectGuardToursStatusFilter,
  selectGuardToursSubmittedAtRangeFilter,
  setGuardToursFacilityIdsFilter,
  setGuardToursGuardIdsFilter,
  setGuardToursStatusFilter,
  setGuardToursSubmittedAtRangeFilter,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { FilterOptionI } from '@/utils/filterDrawers'
import {
  getFacilityOptions,
  getUserOptions,
} from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

interface GuardToursFilterDrawerIProps {
  onClose: () => void
}

interface GuardToursFiltersFormInputsI {
  submittedAt?: FilterOptionI<number | DateValueI>
  facilityName?: MultiFilter
  guardName?: MultiFilter
  status?: MultiFilter
}

export const useGuardToursFilterDrawer = ({
  onClose,
}: GuardToursFilterDrawerIProps) => {
  const toTitleCase = (str: string) =>
    str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())

  const guardTourStatusOptions = Object.values(GuardTourStatus).map(
    (value) => ({
      value: value,
      label: toTitleCase(value),
    })
  )

  const dispatch = useDispatch()

  const [facilitySearchValue, setFacilitySearchValue] = useState('')
  const [guardSearchValue, setGuardSearchValue] = useState('')

  const [debouncedfacilitySearchValue] = useDebounce(
    facilitySearchValue,
    SEARCH_DEBOUNCE_TIME
  )

  const [debouncedGuardSearchValue] = useDebounce(
    guardSearchValue,
    SEARCH_DEBOUNCE_TIME
  )

  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          type: FacilityType.Normal,
          ...(debouncedfacilitySearchValue?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilitySearchValue }
            : {}),
        },
      },
    })

  const guardNameLikeFilter = JSON.parse(
    JSON.stringify({
      OR: [
        {
          firstNameLike: debouncedGuardSearchValue,
        },
        { lastNameLike: debouncedGuardSearchValue },
        { usernameLike: debouncedGuardSearchValue },
        { fullNameLike: debouncedGuardSearchValue },
      ],
    })
  )

  const { data: guardsData, loading: isGuardDataLoading } = useUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        roleName: Role.GUARD,
        ...(debouncedGuardSearchValue?.length >= MIN_CHAR_COUNT
          ? guardNameLikeFilter
          : {}),
      },
    },
  })
  const facilityIdsFilterValue = useSelector(selectGuardToursFacilityIdsFilter)
  const guardIdsFilterValue = useSelector(selectGuardToursGuardIdsFilter)
  const submittedAtFilterValue = useSelector(
    selectGuardToursSubmittedAtRangeFilter
  )
  const statusFilterValue = useSelector(selectGuardToursStatusFilter)

  const facilityOptions = getFacilityOptions(facilitiesData)
  const guardOptions = getUserOptions(guardsData)

  const onSubmit: SubmitHandler<GuardToursFiltersFormInputsI> = async (
    values
  ) => {
    const selectedFacilityIds = values?.facilityName
    const selectedGuardIds = values?.guardName
    const selectedSubmittedAtRange = values?.submittedAt
    const selectedstatus = values?.status

    dispatch(
      setGuardToursFacilityIdsFilter(
        selectedFacilityIds?.length ? selectedFacilityIds : null
      )
    )
    dispatch(
      setGuardToursGuardIdsFilter(
        selectedGuardIds?.length ? selectedGuardIds : null
      )
    )
    dispatch(
      setGuardToursSubmittedAtRangeFilter(selectedSubmittedAtRange || null)
    )
    dispatch(setGuardToursStatusFilter(selectedstatus || null))

    onClose()
  }

  const { control, handleSubmit, watch, setValue } =
    useForm<GuardToursFiltersFormInputsI>({
      reValidateMode: 'onChange',
    })

  const submittedAtFilterCount = watch('submittedAt') == null ? null : 1

  const onClearFilters = () => {
    setValue('facilityName', null)
    setValue('guardName', null)
    setValue('status', null)
    setValue('submittedAt', null)

    trackEvent(`${BUTTON_PRESS} Guard Tours Filter Drawer - Clear Filters`)
  }

  return {
    guardTourStatusOptions,
    isFacilitiesLoading,
    isGuardDataLoading,
    onSubmit,
    onClearFilters,
    submittedAtFilterCount,
    control,
    handleSubmit,
    facilityIdsFilterValue,
    guardIdsFilterValue,
    submittedAtFilterValue,
    statusFilterValue,
    facilityOptions,
    guardOptions,
    facilitySearchValue,
    setFacilitySearchValue,
    guardSearchValue,
    setGuardSearchValue,
    watch,
    setValue,
  }
}
